import React, { useState } from 'react';
import { format, formatDistanceToNow, isFuture } from 'date-fns';
import { stringifyUrl } from 'query-string';
import { upperFirst, lowerCase } from 'lodash';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ClipboardIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery } from '@tanstack/react-query';
import 'react-table/react-table.css';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';

import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { fetcher } from '@dashboard/services/request';
import { Label, Select, Switch } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link/Link';
import { IndustryEnum } from '@dashboard/enums/industry';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { formatPriceInput, formatPriceForInput } from '@dashboard/services/formatting/price';
import { formatPrice } from '@dashboard/services/formatting';

import { METRICS } from '@dashboard/scenes/Dashboard/metrics';
import { dateRanges } from '@dashboard/scenes/Dashboard/date-ranges';
import { getBillingPeriod } from '@dashboard/services/plans';

import Button from '@dashboard/components/Button';
import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import { DescriptionList } from '@dashboard/components/DescriptionList';

import Circle from '../components/Circle';
import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';
import { useAdminGhosting } from '@/dashboard/context/AdminGhostingContext/AdminGhostingContext';
import { confirm } from '@dashboard/lib/confirm';
import { prompt } from '@dashboard/lib/prompt';
import { Form } from '@/dashboard/components/Formik';

export function getRenewalStatus(seller, short = false) {
  const trialEndsAt = seller.trialEnd || null;
  const renewsAt = seller.stripeSubscriptionCurrentPeriodEnd || null;
  const cancelsAt = seller.stripeSubscriptionCancelAt || null;
  const isActive = seller.subscriptionStatus === 'active';
  const isTrialing = seller.subscriptionStatus === 'trialing';
  const isExpired = seller.subscriptionStatus === 'trial_expired';
  const isPastDue = seller.stripeSubscriptionStatus === 'past_due';
  const hasPendingPlanChange = seller.pendingPlanChange?.stripeSubscriptionScheduleId;

  // pending plan changes are always downgrades
  if (hasPendingPlanChange) {
    const pendingPlan = seller.pendingPlanChange.plan;
    if (pendingPlan.handle === 'scale' && pendingPlan.bracket) {
      return `Downgrades to ${pendingPlan.title} ($${pendingPlan.bracket / 1000}K) on ${format(
        new Date(seller.pendingPlanChange.effectiveDate),
        'd MMM, yyyy',
      )}`;
    }

    return `Downgrades to ${seller.pendingPlanChange.plan.title} on ${format(
      new Date(seller.pendingPlanChange.effectiveDate),
      'd MMM, yyyy',
    )}`;
  }

  // canceling in the future
  if (cancelsAt) {
    if (short) {
      return `cancels in ${formatDistanceToNow(new Date(cancelsAt))}`;
    }

    return `cancels on ${format(new Date(cancelsAt), 'd MMM, yyyy')}`;
  }

  // trialing
  if (isFuture(new Date(trialEndsAt)) && isTrialing) {
    if (short) {
      return `trialing, ends in ${formatDistanceToNow(new Date(trialEndsAt))}`;
    }
    return `trialing, ends on ${format(new Date(trialEndsAt), 'd MMM, yyyy')}`;
  }

  // subscribed but still in trial
  if (isFuture(new Date(trialEndsAt)) && isActive) {
    if (short) {
      return `trialing, starts in ${formatDistanceToNow(new Date(trialEndsAt))}`;
    }

    return `trialing, starts on ${format(new Date(trialEndsAt), 'd MMM, yyyy')}`;
  }

  // expired trial
  if (isExpired) {
    if (short) {
      return `trial expired ${formatDistanceToNow(new Date(trialEndsAt))} ago`;
    }

    return `trial expired on ${format(new Date(trialEndsAt), 'd MMM, yyyy')}`;
  }

  // no subscription
  if (!renewsAt) {
    return 'no subscription';
  }

  if (isPastDue) {
    return `past due since ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
  }

  if (short) {
    return `renews in ${formatDistanceToNow(new Date(renewsAt))}`;
  }

  return `renews on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
}

const Merchant = () => {
  const { logAdminInAsSeller, token, user: adminUser } = useAuth();
  const {
    params: { sellerId },
  } = useRouteMatch();
  const history = useHistory();
  const { emitToast } = useToast();
  const [isEditingIndustry, setIsEditingIndustry] = useState(false);
  const [isEditingCustomPricing, setIsEditingCustomPricing] = useState(false);
  const { setAdminGhostingSellerId } = useAdminGhosting();

  const {
    data: user,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: ['merchant', sellerId],
    queryFn: () => fetcher(`/admin/get-user-by-seller/${sellerId}`, token),
  });

  const setMerchantIndustryMutation = useMutation({
    mutationFn: ({ sellerId, industry }) =>
      fetcher(`/admin/sellers/${sellerId}`, token, {
        method: 'POST',
        body: JSON.stringify({ industry }),
      }),
    onSuccess: () => {
      emitToast({
        title: 'Merchant industry updated',
      });
      setIsEditingIndustry(false);
      refetch();
    },
  });

  const disableSellerMutation = useMutation({
    mutationFn: ({ sellerId, reason }) =>
      fetcher(`/admin/sellers/${sellerId}/disable`, token, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      emitToast({
        title: 'Seller disabled',
        description: 'The seller has been disabled and notified via email.',
      });
      refetch();
    },
  });

  const enableSellerMutation = useMutation({
    mutationFn: sellerId =>
      fetcher(`/admin/sellers/${sellerId}/enable`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      emitToast({
        title: 'Seller enabled',
      });
      refetch();
    },
  });

  const updateSellerMutation = useMutation({
    mutationFn: values =>
      fetcher(`/admin/sellers/${sellerId}`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: () => {
      emitToast({
        title: 'Seller updated',
      });
      refetch();
    },
  });

  async function handleLogAdminInAsSeller(sellerId, path = '/') {
    await logAdminInAsSeller(sellerId);
    setAdminGhostingSellerId(sellerId);

    emitToast({
      title: `Logged in as merchant`,
    });

    history.push(path);
  }

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () =>
      fetcher(`/admin/sellers/${sellerId}/cancel-subscription`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      emitToast({
        title: 'Subscription cancelled',
      });
      refetch();
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const expireTrialMutation = useMutation({
    mutationFn: () =>
      fetcher(`/admin/sellers/${sellerId}/expire-trial`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      emitToast({
        title: 'Trial expired',
      });
      refetch();
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const [extendTrialDays, setExtendTrialDays] = useState(1);

  const extendTrialMutation = useMutation({
    mutationFn: () =>
      fetcher(`/admin/sellers/${sellerId}/extend-trial`, token, {
        method: 'POST',
        body: JSON.stringify({ days: extendTrialDays }),
      }),
    onSuccess: () => {
      emitToast({
        title: 'Trial extended',
      });
      refetch();
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: () =>
      fetcher(`/admin/users/${user._id}/delete`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      emitToast({
        title: 'User deleted',
      });
      history.push('/admin/merchants');
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const [statisticsRange, setStatisticsRange] = React.useState('lastHour');
  const [metric, setMetric] = React.useState('');

  const startStatisticsAggregate = useMutation({
    mutationFn: () =>
      fetcher(
        stringifyUrl({
          url: `/admin/sellers/${sellerId}/aggregate-historical-statistics`,
          query: {
            range: statisticsRange,
            ...(metric && { metric }),
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      emitToast({
        title: 'Statistics aggregation started',
        type: 'success',
      });
    },
    onError: error => {
      emitToast({
        title: 'Error starting statistics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const [metricsRange, setMetricsRange] = React.useState('lastHour');

  const startMetricsAggregation = useMutation({
    mutationFn: () =>
      fetcher(
        stringifyUrl({
          url: `/admin/sellers/${sellerId}/aggregate-historical-metrics`,
          query: {
            range: metricsRange,
          },
        }),
        token,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      emitToast({
        title: 'Metrics aggregation started',
      });
    },
    onError: error => {
      emitToast({
        title: 'Error starting metrics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  async function importStripeSubscriptions(sellerId) {
    // confirm actions
    const confirmed = await confirm(
      "This will import this merchant's Stripe subscriptions and collect fees on them. Only do this after a merchant has agreed. Are you sure you want to continue?",
    );

    if (!confirmed) {
      return;
    }

    const { data } = await fetcher(
      `/admin/sellers/${sellerId}/import-stripe-subscriptions`,
      token,
      { method: 'POST' },
    );

    return data;
  }

  async function disableSeller(sellerId) {
    // confirm actions
    const reason = await prompt(
      'Are you sure you want to disable this seller? They will no longer be able to log in. Enter a reason:',
      {
        placeholder: 'Reason',
      },
    );

    if (!reason) {
      return;
    }

    disableSellerMutation.mutate({ sellerId, reason });
  }

  async function enableSeller(sellerId) {
    // confirm actions
    const confirmed = await confirm(
      'Are you sure you want to enable this seller? They will be able to log in again.',
    );

    if (!confirmed) {
      return;
    }

    enableSellerMutation.mutate(sellerId);
  }

  if (!user || !user.seller) {
    if (isLoading) {
      return 'Loading...';
    } else {
      return 'Not found';
    }
  }

  const sellerOwnerEmail = user.seller.users.find(user => user.role === 'owner')?.user.email;
  const crispSellerProfileUrl = `https://app.crisp.chat/website/abe8e9e1-02b6-4715-b289-7573fd280b36/contacts?query=${sellerOwnerEmail}`;

  return (
    <div className="mt-8 space-y-8">
      {user.seller.disabled && (
        <div className="col-span-2">
          <div className="bg-red-50 border border-red-200 text-red-500 px-4 py-3 rounded relative">
            <strong className="font-bold">Merchant is disabled.</strong>
            <span className="block sm:inline"> Reason: {user.seller.disabledReason}</span>
          </div>
        </div>
      )}

      <div className="flex gap-x-4">
        <Button
          handleClick={() => handleLogAdminInAsSeller(user.seller._id, '/')}
          appearance="primary"
        >
          View dashboard as merchant
        </Button>

        <Button
          handleClick={() => handleLogAdminInAsSeller(user.seller._id, '/pages')}
          appearance="primary"
        >
          View pages as merchant
        </Button>

        <Button href={crispSellerProfileUrl} appearance="primary" target="_blank" external>
          Open in Crisp
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-16">
        <div>
          <h1 className="mb-4">{user.seller.displayName}</h1>
          <div className="max-w-screen-sm">
            <DescriptionList.List>
              <DescriptionList.Item>
                <DescriptionList.Term>Store name</DescriptionList.Term>
                <DescriptionList.Description>
                  {user.seller.displayName || 'N/A'}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Store focus</DescriptionList.Term>
                <DescriptionList.Description>
                  {isEditingIndustry ? (
                    <Select
                      onChange={e =>
                        setMerchantIndustryMutation.mutate({
                          sellerId: user.seller._id,
                          industry: e.target.value,
                        })
                      }
                    >
                      <option>Industry</option>

                      {Object.values(IndustryEnum).map(industry => (
                        <option
                          key={industry}
                          value={industry}
                          selected={industry === user.seller.industry}
                        >
                          {upperFirst(industry.replace(/-/g, ' '))}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <div>
                      <span className="mr-2">
                        {user.seller.industry
                          ? upperFirst(user.seller.industry.replace(/-/g, ' '))
                          : 'N/A'}
                      </span>
                      <Button appearance="icon-button" onClick={() => setIsEditingIndustry(true)}>
                        <PencilIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Website</DescriptionList.Term>
                <DescriptionList.Description>
                  <Link href={user.seller.url} blank>
                    {user.seller.url}
                  </Link>
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Signed up at</DescriptionList.Term>
                <DescriptionList.Description>
                  {format(new Date(user.createdAt), 'd MMM, yyyy, HH:mm')}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Last active at</DescriptionList.Term>
                <DescriptionList.Description>
                  {format(new Date(user.lastActivity), 'd MMM, yyyy, HH:mm')}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Store ID</DescriptionList.Term>
                <DescriptionList.Description>
                  <CopyTextWithTooltip text={user.seller._id}>
                    <div className="flex gap-x-1 items-center">
                      <span>{user.seller._id}</span>
                      <ClipboardIcon className="w-4 h-4" />
                    </div>
                  </CopyTextWithTooltip>
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Stripe Account</DescriptionList.Term>
                <DescriptionList.Description>
                  <div className="flex items-center gap-x-2">
                    <Circle color={checkIsConnectedToStripe(user.seller) ? 'green' : 'red'} />
                    Connected to Stripe
                  </div>

                  {user.seller.paymentGateways?.stripe?.userId && (
                    <>
                      <CopyTextWithTooltip text={user.seller.paymentGateways?.stripe?.userId}>
                        <div className="flex gap-x-1 items-center">
                          <span>{user.seller.paymentGateways?.stripe?.userId}</span>
                          <ClipboardIcon className="w-4 h-4" />
                        </div>
                      </CopyTextWithTooltip>

                      <br />
                      <Button
                        appearance="light"
                        to={getStripeDashboardUrl(
                          `/connect/accounts/${user.seller.paymentGateways.stripe.userId}`,
                        )}
                        target="_blank"
                        size="small"
                      >
                        View Stripe Connect Account
                      </Button>
                    </>
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Stripe Customer</DescriptionList.Term>
                <DescriptionList.Description>
                  {user.seller.stripeCustomerId ? (
                    <CopyTextWithTooltip text={user.seller.stripeCustomerId}>
                      <div className="flex gap-x-1 items-center">
                        <span>{user.seller.stripeCustomerId}</span>
                        <ClipboardIcon className="w-4 h-4" />
                      </div>
                    </CopyTextWithTooltip>
                  ) : (
                    'N/A'
                  )}

                  {user.seller.stripeCustomerId && (
                    <>
                      <br />
                      <Button
                        appearance="light"
                        to={getStripeDashboardUrl(`/customers/${user.seller.stripeCustomerId}`)}
                        target="_blank"
                        size="small"
                      >
                        View Stripe Customer
                      </Button>
                    </>
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Postmark Server</DescriptionList.Term>
                <DescriptionList.Description>
                  {user.seller.senderServer ? (
                    <Button
                      href={`https://account.postmarkapp.com/servers/${user.seller.senderServer?.id}/streams/outbound/events`}
                      target="_blank"
                      appearance="secondary"
                      size="small"
                    >
                      View Postmark Server
                    </Button>
                  ) : (
                    'N/A'
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>
            </DescriptionList.List>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="mb-4">Subscription</h2>
            <div className="flex flex-col items-start gap-y-2">
              <div className="flex items-center gap-x-2">
                <Circle
                  color={
                    user.seller.subscriptionStatus === 'trialing' ||
                    user.seller.stripeSubscriptionStatus === 'past_due'
                      ? 'orange'
                      : user.seller.subscriptionStatus === 'active'
                        ? 'green'
                        : 'red'
                  }
                />
                {user.seller.plan?.title} {getBillingPeriod(user.seller.planBillingPeriod)} (
                {getRenewalStatus(user.seller)})
              </div>
            </div>

            {!!(user.seller.plan?.volumeLimit || user.seller.plan?.customPrice) && (
              <div className="max-w-lg mb-4">
                <p className="text-sm text-gray-500 mb-1">
                  This merchant has a custom pricing agreement.
                </p>
                <ul className="list-disc ml-5 text-sm text-gray-500">
                  <li>
                    Volume limit:{' '}
                    {user.seller.plan.volumeLimit
                      ? formatPrice({
                          amount: user.seller.plan.volumeLimit,
                          currency: 'usd',
                        })
                      : 'Not set'}
                  </li>
                  <li>
                    Custom price:{' '}
                    {user.seller.plan.customPrice
                      ? formatPrice({
                          amount: user.seller.plan.customPrice,
                          currency: 'usd',
                        })
                      : 'Not set'}
                  </li>
                  <li>
                    Plan renews on:{' '}
                    {user.seller.stripeSubscriptionCurrentPeriodEnd
                      ? format(
                          new Date(user.seller.stripeSubscriptionCurrentPeriodEnd),
                          'd MMM, yyyy, HH:mm',
                        )
                      : 'Not set'}
                  </li>
                </ul>
              </div>
            )}

            {adminUser.role === AdminRolesEnum.ADMIN && (
              <div className="max-w-lg">
                <Switch
                  onChange={() => setIsEditingCustomPricing(!isEditingCustomPricing)}
                  value={isEditingCustomPricing}
                  label="Edit custom pricing agreement"
                  size="medium"
                />

                {isEditingCustomPricing && (
                  <div className="mt-2">
                    <Form
                      initialValues={{
                        plan: {
                          volumeLimit: user.seller.plan?.volumeLimit
                            ? formatPriceForInput({
                                amount: user.seller.plan.volumeLimit,
                                currency: 'usd',
                                style: 'decimal',
                              })
                            : '',
                          customPrice: user.seller.plan?.customPrice
                            ? formatPriceForInput({
                                amount: user.seller.plan.customPrice,
                                currency: 'usd',
                                style: 'decimal',
                              })
                            : '',
                        },
                      }}
                      fields={[
                        {
                          name: 'plan.volumeLimit',
                          type: 'number',
                          label: 'Volume limit',
                          labelDescription: 'Maximum amount of revenue per year',
                          placeholder: '10000.00',
                          required: false,
                          step: 'any',
                          helpText: values =>
                            values.plan.volumeLimit
                              ? `${formatPrice({
                                  amount: formatPriceInput(values.plan.volumeLimit, 'usd'),
                                  currency: 'usd',
                                })}`
                              : null,
                        },
                        {
                          name: 'plan.customPrice',
                          type: 'number',
                          label: 'Custom price (per year)',
                          placeholder: '199.00',
                          required: false,
                          step: 'any',
                          helpText: values =>
                            values.plan.customPrice
                              ? `${formatPrice({
                                  amount: formatPriceInput(values.plan.customPrice, 'usd'),
                                  currency: 'usd',
                                })}`
                              : null,
                        },
                      ]}
                      onSubmit={async (values, actions) => {
                        try {
                          const submissionData = {
                            plan: {
                              ...user.seller.plan,
                              volumeLimit: values.plan.volumeLimit
                                ? formatPriceInput(values.plan.volumeLimit, 'usd')
                                : null,
                              customPrice: values.plan.customPrice
                                ? formatPriceInput(values.plan.customPrice, 'usd')
                                : null,
                            },
                          };

                          const data = await updateSellerMutation.mutateAsync(submissionData);

                          actions.resetForm({
                            values: {
                              plan: {
                                volumeLimit: data.plan?.volumeLimit
                                  ? formatPriceForInput({
                                      amount: data.plan.volumeLimit,
                                      currency: 'usd',
                                      style: 'decimal',
                                    })
                                  : '',
                                customPrice: data.plan?.customPrice
                                  ? formatPriceForInput({
                                      amount: data.plan.customPrice,
                                      currency: 'usd',
                                      style: 'decimal',
                                    })
                                  : '',
                              },
                            },
                          });

                          return {
                            data: {
                              plan: data.plan,
                            },
                          };
                        } catch (error) {
                          return { error };
                        }
                      }}
                      submitButton={{
                        label: 'Save',
                        appearance: 'secondary',
                        size: 'small',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <h2 className="mb-4">Actions</h2>

            <div className="space-y-8">
              <div className="">
                <Label htmlFor="subscriptionStatus">
                  Change subscription status: {user.seller.subscriptionStatus} (
                  {getRenewalStatus(user.seller)})
                </Label>

                <div className="space-x-2">
                  {adminUser.role === AdminRolesEnum.ADMIN && (
                    <Button
                      onClick={async () => {
                        const confirmed = await confirm(
                          'Are you sure you want to cancel this subscription?',
                        );

                        if (!confirmed) {
                          return;
                        }

                        cancelSubscriptionMutation.mutate();
                      }}
                      disabled={cancelSubscriptionMutation.isLoading}
                      appearance="secondary"
                      size="small"
                    >
                      Cancel subscription
                    </Button>
                  )}

                  <Button
                    onClick={async () => {
                      const confirmed = await confirm(
                        'Are you sure you want to expire this trial?',
                      );

                      if (!confirmed) {
                        return;
                      }

                      expireTrialMutation.mutate();
                    }}
                    disabled={expireTrialMutation.isLoading}
                    appearance="secondary"
                    size="small"
                  >
                    Expire trial
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <Label>Extend trial</Label>

              <div className="flex gap-x-2">
                <div>
                  <Select
                    onChange={e => setExtendTrialDays(parseInt(e.target.value))}
                    value={extendTrialDays}
                  >
                    <option value="1">1 day</option>
                    <option value="2">2 day</option>
                    <option value="3">3 days</option>
                    <option value="4">4 days</option>
                    <option value="5">5 days</option>
                    <option value="6">6 days</option>
                    <option value="7">7 days</option>
                  </Select>
                </div>

                <Button
                  onClick={async () => {
                    const confirmed = await confirm('Are you sure you want to extend this trial?');

                    if (!confirmed) {
                      return;
                    }

                    extendTrialMutation.mutate(extendTrialDays);
                  }}
                  disabled={extendTrialMutation.isLoading}
                  appearance="secondary"
                  size="small"
                >
                  Extend trial
                </Button>
              </div>
            </div>

            <div className="flex gap-x-2 mt-4">
              {adminUser.role === AdminRolesEnum.ADMIN && (
                <>
                  {user.seller.disabled ? (
                    <Button
                      onClick={() => enableSeller(user.seller._id)}
                      appearance="light"
                      size="small"
                    >
                      Enable merchant
                    </Button>
                  ) : (
                    <Button
                      onClick={() => disableSeller(user.seller._id)}
                      appearance="light"
                      size="small"
                    >
                      Disable merchant
                    </Button>
                  )}
                </>
              )}

              {adminUser.role === AdminRolesEnum.ADMIN && (
                <>
                  <Button
                    onClick={async () => {
                      const confirmed1 = await confirm(
                        'Are you sure you want to delete this user? This is irreversible.',
                      );

                      if (!confirmed1) {
                        return;
                      }

                      const confirmed2 = await confirm(
                        'Are you ABSOLUTELY sure you want to delete this user?',
                      );

                      if (!confirmed2) {
                        return;
                      }

                      deleteUserMutation.mutate(user._id);
                    }}
                    appearance="light"
                    size="small"
                  >
                    Delete user
                  </Button>
                  <Button
                    handleClick={() => importStripeSubscriptions(user.seller._id)}
                    appearance="light"
                    disabled={!checkIsConnectedToStripe(user.seller)}
                    size="small"
                  >
                    Import Stripe Subscriptions
                  </Button>
                </>
              )}
            </div>

            {adminUser.role === AdminRolesEnum.ADMIN && (
              <>
                <div className="mt-4">
                  <Label>Aggregate historical statistics</Label>
                  <div className="flex gap-x-2">
                    <div>
                      <Select onChange={e => setStatisticsRange(e.target.value)}>
                        <option disabled>Range</option>
                        <option value="lastHour" selected={statisticsRange === 'lastHour'}>
                          Last hour
                        </option>
                        {Object.keys(dateRanges).map(range => (
                          <option value={range} selected={range === statisticsRange} key={range}>
                            {dateRanges[range].label}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <Select onChange={e => setMetric(e.target.value)}>
                        <option disabled>Metric</option>
                        <option value="" selected={metric === ''}>
                          All metrics
                        </option>
                        {Object.entries(METRICS).map(([key, value]) => (
                          <option value={value} selected={value === metric} key={value}>
                            {upperFirst(lowerCase(key))}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <Button
                      onClick={() => startStatisticsAggregate.mutate()}
                      appearance="secondary"
                      size="small"
                    >
                      Start statistics aggregation
                    </Button>
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Aggregate historical metrics</Label>
                  <div className="flex gap-x-2">
                    <div>
                      <Select onChange={e => setMetricsRange(e.target.value)}>
                        <option disabled>Range</option>
                        <option value="lastHour" selected={metricsRange === 'lastHour'}>
                          Last hour
                        </option>
                        {Object.keys(dateRanges).map(range => (
                          <option value={range} selected={range === metricsRange} key={range}>
                            {dateRanges[range].label}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <Button
                      onClick={() => startMetricsAggregation.mutate()}
                      appearance="secondary"
                      size="small"
                    >
                      Start metrics aggregation
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div>
        {/* Team members table with name + role + email address + created date */}
        <h2 className="mb-4">Team members ({user.seller.users.length})</h2>

        <table className="w-full">
          <thead>
            <tr>
              <th className="text-left">Name</th>
              <th className="text-left">Email</th>
              <th className="text-left">Role</th>
              <th className="text-left">Created at</th>
              <th className="text-left">Last active at</th>
              <th className="text-left">User ID</th>
            </tr>
          </thead>
          <tbody>
            {user.seller.users?.map(teamMember => (
              <tr key={teamMember._id}>
                <td className="py-2">{teamMember.user.name}</td>
                <td>
                  <CopyTextWithTooltip text={teamMember.user.email}>
                    <div className="flex gap-x-1 items-center">
                      <span>{teamMember.user.email}</span>
                      <ClipboardIcon className="w-4 h-4" />
                    </div>
                  </CopyTextWithTooltip>

                  <Button
                    href={`https://app.crisp.chat/website/abe8e9e1-02b6-4715-b289-7573fd280b36/contacts?query=${teamMember.user.email}`}
                    target="_blank"
                    appearance="secondary"
                    size="small"
                    className="ml-2"
                    external
                  >
                    Open in Crisp
                  </Button>
                </td>
                <td>{teamMember.role}</td>
                <td>{format(new Date(teamMember.user.createdAt), 'd MMM, yyyy, HH:mm')}</td>
                <td>{format(new Date(teamMember.user.lastActivity), 'd MMM, yyyy, HH:mm')}</td>
                <td>
                  <CopyTextWithTooltip text={teamMember.user._id}>
                    <div className="flex gap-x-1 items-center">
                      <span>{teamMember.user._id}</span>
                      <ClipboardIcon className="w-4 h-4" />
                    </div>
                  </CopyTextWithTooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="mb-4">Feature flags</h2>

        <Form
          initialValues={{
            labs: {
              canUseApi: user.seller.labs?.canUseApi,
              slack: user.seller.labs?.slack,
              couponTicketTypes: user.seller.labs?.couponTicketTypes,
            },
          }}
          fields={[
            {
              name: 'labs.canUseApi',
              type: 'checkbox',
              label: 'API / Webhooks',
              required: false,
            },
            {
              name: 'labs.slack',
              type: 'checkbox',
              label: 'Slack integration',
              required: false,
            },
            {
              name: 'labs.couponTicketTypes',
              type: 'checkbox',
              label: 'Coupon ticket types',
              required: false,
            },
          ]}
          onSubmit={async (values, actions) => {
            try {
              const data = await updateSellerMutation.mutateAsync(values);

              actions.resetForm({
                values: {
                  labs: data.labs,
                },
              });

              return {
                data: {
                  labs: data.labs,
                },
              };
            } catch (error) {
              return { error };
            }
          }}
          submitButton={{
            label: 'Save',
            appearance: 'secondary',
            size: 'small',
          }}
        />
      </div>
    </div>
  );
};

export default Merchant;
